<template>
  <div :class="classes">
    <!-- Weekday header -->
    <div class="asd20-calendar__weekdays">
      <div
        v-for="(dayName, index) in weekdayNames"
        :key="index"
        class="asd20-calendar__weekday"
      >
        <span>{{ shortWeekdays ? dayName.charAt(0) : dayName }}</span>
      </div>
    </div>
    <!-- Days -->
    <ol class="asd20-calendar__days">
      <li
        class="asd20-calendar__day"
        v-for="(day, index) in days"
        :key="index"
        :class="{
          selected: day === selectedDay,
          today: day.today,
          outside: day.outsideOfCurrentMonth,
          empty: day.events.length === 0,
        }"
        @click="onClickDay($event, day)"
      >
        <div class="asd20-calendar__date">
          <span class="month">{{ day.month }}</span>
          <span class="day">{{ day.number }}</span>
          <span class="year">{{ day.year }}</span>
        </div>

        <div
          class="asd20-calendar__events"
          v-if="!annualView || !day.outsideOfCurrentMonth"
        >
          <Asd20CalendarEventButton
            v-for="(event, index) in day.events"
            :key="index"
            :event="event"
            :class="
              day.events.length === 2
                ? 'small-dot'
                : day.events.length > 2
                ? 'smaller-dot'
                : ''
            "
            @click.native="onEventClick(event)"
          />
        </div>
      </li>
    </ol>
    <asd20-viewport
      class="asd20-calendar__day-events"
      scrollable
      v-if="selectedDay"
    >
      <Asd20CalendarEventButton
        v-for="(event, index) in selectedDay.events"
        :key="index"
        :event="event"
        @click.native="onEventClick(event)"
        large
      />
    </asd20-viewport>
    <Asd20EventModal
      :event="selectedEvent"
      :open="!!selectedEvent"
      @dismiss="selectedEvent = null"
    />
  </div>
</template>

<script>
import Asd20CalendarEventButton from '../../atoms/Asd20CalendarEventButton'
import Asd20EventModal from '../../organisms/Asd20EventModal'
import Asd20Viewport from '../../atoms/Asd20Viewport'
import expandEvents from '../../../helpers/expandEvents'
import mapEventsToDays from '../../../helpers/mapEventsToDays'

export default {
  name: 'Asd20Calendar',
  components: { Asd20CalendarEventButton, Asd20EventModal, Asd20Viewport },
  props: {
    events: { type: Array, default: () => [] },
    showFull: { type: Boolean, default: true },
    showAll: { type: Boolean, default: false },
    month: { type: Number, default: new Date().getMonth() },
    year: { type: Number, default: new Date().getFullYear() },
    absolute: { type: Boolean, default: true },
    shortWeekdays: { type: Boolean, default: false },
    annualView: { type: Boolean, default: false },
  },

  data: () => ({
    loadingEvents: false,
    selectedEvent: null,
    selectedDay: null,
  }),

  watch: {
    days: {
      handler: function() {
        this.selectedDay =
          this.days.find(day => {
            return day.today
          }) || null
      },
      immediate: true,
    },
  },

  computed: {
    classes() {
      return {
        'asd20-calendar': true,
        'asd20-calendar--absolute': this.absolute,
        'asd20-calendar--annual': this.annualView,
      }
    },
    days() {
      return mapEventsToDays(
        expandEvents(this.events, this.annualView),
        this.year,
        this.month,
      )
    },
    weekdayNames() {
      return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    },
  },
  methods: {
    onEventClick(event) {
      this.selectedEvent = event
    },
    onClickDay(event, day) {
      event.stopPropagation()
      this.selectedDay = day
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../design/_mixins.scss';
@import '../../../design/_variables.scss';

$base: var(--website-card__reverse-background-color);
$primary: var(--color__primary);
$accent: var(--color__accent);

.asd20-calendar {
  position: relative;
  display: flex;
  flex-direction: column;

  &--absolute {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }

  &__weekdays {
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-grow: 0;
    flex-shrink: 0;
    width: 100%;
    height: space(0.875);
    margin-bottom: space(0.125);
    padding: 0 space(0.125);
    background: var(--color__accent-t80);
    color: var(--color__primary);
    font-size: 0.875rem;
    line-height: 1;
  }

  &__day-events {
    flex-grow: 1;
    &::v-deep .asd20-calendar-event-button:not(:last-of-type) {
      // margin-top: space(0.125);
      margin-bottom: space(0.125);
      // border-bottom: 1px solid var(--color__tertiary);
    }
  }

  &__days {
    position: relative;
    list-style: none;
    margin: 0 0 0 0;
    padding: 0;
    position: relative;
    flex-grow: 0;
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
    flex-wrap: wrap;
    height: 40%;
    width: 100%;
    flex-shrink: 0;
  }
  &__day {
    position: relative;
    font-size: 1rem;
    margin-bottom: 0;
    padding: 0;
    width: calc(100% / 7);
    flex-grow: 1;

    box-shadow: 0 -1px 0 0 var(--color__tertiary) inset,
      -1px 0 0 0 var(--color__tertiary) inset;

    &:nth-child(7n) {
      box-shadow: 0 -1px 0 0 var(--color__tertiary) inset;
    }

    &.today {
      background: var(--color__accent-t90);
      .day {
        background: transparent;
        color: var(--color__primary);
        width: auto;
        justify-content: space-between;
        margin-right: 0;
      }
    }

    &.outside {
      background: var(--website-page__alternate-background-t70);
    }

    &.selected {
      outline: 2px solid var(--color__accent);
      outline-offset: -2px;
      z-index: 1;
    }
  }

  &__date {
    position: absolute;
    top: 0;
    font-size: 1rem;
    line-height: 1;

    .weekday,
    .month,
    .year {
      display: none;
    }
    .day {
      display: flex;
      align-items: center;
      justify-content: center;
      width: space(0.75);
      height: space(0.75);
      padding: space(0.0625);
      font-size: 0.875rem;
      line-height: 1;
    }
  }

  &__events {
    box-sizing: border-box;
    position: absolute;
    left: 0;
    bottom: 0;
    right: 0;
    top: space(0.875);
    margin-bottom: 0;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    justify-content: flex-start;
    padding-left: space(0.125);
    &::v-deep .asd20-calendar-event-button {
      pointer-events: none;
      padding: space(0.125) space(0.25);
      margin-top: 0;

      .title {
        font-size: 0.75rem;
        line-height: 1.25;
        font-weight: normal;
        color: currentColor;
        // white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
  }
  &--annual {
    .asd20-calendar__weekdays {
      padding: 0;
    }
    .asd20-calendar__days {
      position: relative;
      list-style: none;
      margin: 0 0 0 0;
      padding: 0;
      position: relative;
      flex-grow: 0;
      display: flex;
      justify-content: flex-start;
      align-items: stretch;
      flex-wrap: wrap;
      height: 40%;
      width: 100%;
      flex-shrink: 0;
    }

    .asd20-calendar__day {
      position: relative;
      font-size: 1rem;
      margin-bottom: 0;
      padding: 0;
      width: calc(100% / 7);
      flex-grow: 1;
      display: flex;
      flex-direction:column;
      align-items: flex-start;
      background-color: var(--website-page__alternate-background-t70);
      min-height: 2.5rem;
      outline: 1px solid var(--color__tertiary);
      border: none;
      box-shadow: none;
      &.today {
        &::v-deep .day::after {
          content: none;
          display: none;
        }
      }
    }
    .asd20-calendar__day-events {
      display: none !important;
    }

    .asd20-calendar__date {
      position: static;
      font-size: 0.875rem;
      margin: 0 0 0.125rem 0.125rem;

      .day {
        display: inline;
        width: auto;
        height: auto;
        padding: 0;
        font-size: 0.875rem;
      }

      .weekday,
      .month,
      .year {
        display: none;
      }
    }

    .asd20-calendar__events {
      position: static;
      display: flex;
      flex-direction: column;
      width: 100%;
      justify-content: stretch;
      max-height: 2rem;

      &::v-deep .asd20-calendar-event-button {
        background: transparent;
        padding: 0;
        // width: space(0.375);
        // height: space(0.375);
        width: max-content;
        height: max-content;
        // margin-right: space(0.0625);
        .title::before {
          width: 4rem;
          height: 2rem;
          margin: 0;
          border-radius: 0;

        }
        .title span {
          display: none;
        }
      }
      &::v-deep .small-dot {
        width: 4rem;
        height: 50%;
        // .title::before {
        //   width: 12px;
        //   height: 30px;
        // }
      }
      &::v-deep .smaller-dot {
        width: 4rem;
        height: 33%;
        // .title::before {
        //   width: 12px;
        //   height: 30px;
        // }
      }
    }
    .outside {
      .asd20-calendar__date .day {
        display: none;
      }
    }
  }

}

@media (max-width: 767px) {
  .asd20-calendar__events {
    &::v-deep .asd20-calendar-event-button {
      background: transparent;
      padding: 0;
      width: space(0.25);
      height: space(0.25);
      margin-right: space(0.0625);
      &::before {
        margin: 0;
      }
      .title span {
        display: none;
      }
    }
  }
}

@media (min-width: 768px) {
  .asd20-calendar {
    // padding: space(1);
    &__days {
      height: 100%;
      width: 100%;
    }
    &__day-events {
      display: none;
    }
    &__day {
      position: relative;
      // height: calc(100vh / 5.25);
      // min-height: calc(100% / 5);

      &:nth-child(n + 1):nth-child(-n + 7) {
        // margin-top: space(1);

        // box-shadow: 0 -1px 0 0 var(--color__tertiary),
        //   0 1px 0 0 var(--color__tertiary), -1px 0 0 0 var(--color__tertiary);

        &::v-deep .asd20-calendar__date {
          width: 100%;
        }
        &::v-deep .asd20-calendar__date .weekday {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: absolute;
          top: space(-1);
          height: space(1);
          width: 100%;
          overflow: hidden;
          background: var(--color__tertiary);
          // text-transform: uppercase;
          // padding: space(0.25) 0 0 space(0.25);
          color: $primary;
          text-overflow: ellipsis;
        }
      }

      // &:nth-child(n + 1):nth-child(1) {
      //   box-shadow: 0 -1px 0 0 var(--color__tertiary),
      //     0 1px 0 0 var(--color__tertiary);
      // }

      &.selected {
        // outline: none;
      }

      &.outside {
      }

      &.today {
        &::v-deep .day {
          &::after {
            content: 'Today';
            margin-left: space(0.25);
            font-size: 0.75em;
          }
        }
      }
    }
    &__events {
      overflow-y: auto;
      padding-left: 0;
      flex-direction: column;
      flex-wrap: nowrap;
      &::v-deep .asd20-calendar-event-button {
        pointer-events: all;
        flex-grow: 0;
      }
    }
  }
}

@media (min-width: 1440px) {
  .asd20-calendar {
    // padding: space(2);
    &__date {
      position: absolute;
      top: 0;
      font-size: 1rem;
      line-height: 1;

      &:after {
        display: none;
      }
      .weekday,
      .month,
      .year {
        display: none;
      }
      .day {
        display: flex;
        align-items: center;
        justify-content: center;
        width: space(1);
        height: space(1);
        padding: space(0.125);
        font-size: 1rem;
      }
    }
    &__events {
      top: space(1.25);
    }
  }
}

</style>
